// PurchaseOrderShipToParty.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Hooks
import useSubscribedNode from 'hooks/useSubscribedNode';
import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';

// Utilities
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { getNodelistNodeTitle } from 'utilities/NodelistField';
import {
  getPurchaseOrderSchemaField,
  getPurchaseOrderShipToKey,
  getShipToPartyDisplay,
} from 'nodetypes/purchaseorder/view/purchaseOrderPartyUtils';

// CSS
import './PurchaseOrderGeneralInfo.css';

// Components
import SelectInput from 'components/selectinput/SelectInput';
import PurchaseOrderPartyDetails from 'nodetypes/purchaseorder/view/PurchaseOrderPartyDetails';

const CONTACT_TYPE_KEY = 'CONTACT';

/**
 * PurchaseOrderShipToParty
 *
 * Houzz-style single ship-to contact picker + hydrated address block.
 */
const PurchaseOrderShipToParty = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
  relatedNodesByKey = null,
}) => {
  // ----- CONTEXT ----- //

  const { activeBase } = useBase();

  const baseKey = node?.baseKey || activeBase?.key || null;
  const typeKey = String(nodeType?.key || node?.type || 'PURCHASE_ORDER').trim().toUpperCase();
  const shipToField = useMemo(
    () => getPurchaseOrderSchemaField(nodeType, 'shipTo'),
    [nodeType],
  );
  const targetType = String(shipToField?.targetType || CONTACT_TYPE_KEY).trim() || CONTACT_TYPE_KEY;
  const selectedKey = getPurchaseOrderShipToKey(node, nodeType);
  const canEdit = Boolean(!readOnly && typeof onNodeChange === 'function' && node?.key);
  const overrideNode = relatedNodesByKey && selectedKey
    ? (relatedNodesByKey[selectedKey] || null)
    : null;

  // ----- HOOKS ----- //

  const { options: contactOptions } = useNodelistTargetOptions({
    targetType,
    baseKey,
    enabled: Boolean(canEdit && baseKey && targetType),
  });

  const subscribedContact = useSubscribedNode(selectedKey, {
    enabled: Boolean(selectedKey) && !overrideNode,
  });
  const selectedContact = overrideNode || subscribedContact;

  // ----- MEMOS ----- //

  const display = useMemo(
    () => getShipToPartyDisplay(selectedContact),
    [selectedContact],
  );

  const selectOptions = useMemo(() => {
    const empty = { value: '', label: 'Select a contact…' };
    const byValue = new Map(
      (Array.isArray(contactOptions) ? contactOptions : []).map((option) => [
        String(option.value || '').trim(),
        option,
      ]),
    );

    if (selectedKey && !byValue.has(selectedKey)) {
      byValue.set(selectedKey, {
        value: selectedKey,
        label: selectedContact
          ? getNodelistNodeTitle(selectedContact)
          : 'Unavailable contact',
      });
    }

    return [empty, ...byValue.values()];
  }, [contactOptions, selectedContact, selectedKey]);

  // ----- HANDLERS ----- //

  const handleShipToChange = useCallback((event) => {
    if (!canEdit) return;
    const nextKey = String(event?.target?.value ?? event ?? '').trim();
    const field = shipToField
      ? { ...shipToField, allowMultiple: false }
      : {
        name: 'shipTo',
        source: typeKey.toLowerCase(),
        type: 'relation',
        targetType,
        allowMultiple: false,
      };
    const patch = buildNodeFieldPatch(
      node,
      field,
      typeKey,
      nextKey ? [nextKey] : [],
    );
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, node, onNodeChange, shipToField, targetType, typeKey]);

  // ----- RENDER ----- //

  return (
    <div className="po-general-info-party">
      <div className="po-general-info-label">Ship To</div>

      {canEdit ? (
        <div className="po-general-info-select">
          <SelectInput
            value={selectedKey}
            options={selectOptions}
            placeholder="Select a contact…"
            showIcons={false}
            width="100%"
            hybrid
            style={{
              fontWeight: 500,
              height: '32px',
            }}
            onChange={handleShipToChange}
          />
        </div>
      ) : (
        <div className={`po-general-info-value${display.title ? '' : ' is-empty'}`}>
          {display.title || (canEdit ? 'Select a contact…' : '—')}
        </div>
      )}

      <PurchaseOrderPartyDetails
        relatedNode={selectedContact}
        address={display.address}
        phone={display.phone}
        email={display.email}
        editLabel="Edit contact"
      />
    </div>
  );
});

PurchaseOrderShipToParty.displayName = 'PurchaseOrderShipToParty';

export default PurchaseOrderShipToParty;
